import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion, onDocumentLoaded } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogComponent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();
  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;
  #resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      this.style.setProperty('--predictive-search-results-height', `${entry.contentRect.height}px`);
    }
  });

  /**
   * In-memory storage for recent searches
   * @type {string[]}
   */
  #recentSearches = [];

  /**
   * Cuando se retrasa el JS global, `Theme` puede no existir todavía.
   * Este helper evita ReferenceError en módulos y permite esperar un poco.
   * @param {number} [timeoutMs=2500]
   * @returns {Promise<{ routes?: Record<string, string> } | null>}
   */
  async #waitForTheme(timeoutMs = 2500) {
    const win = /** @type {any} */ (globalThis);

    /** @returns {{ routes?: Record<string, string> } | null} */
    const readTheme = () => {
      const fromWindow = win.Theme;
      if (fromWindow?.routes) return fromWindow;

      // `Theme` puede estar definido como binding global (p.ej. `const Theme = {...}`)
      // y en ese caso NO aparece en `window.Theme`.
      try {
        // eslint-disable-next-line no-undef
        if (typeof Theme !== 'undefined' && Theme?.routes) return Theme;
      } catch {
        // ignore
      }

      return null;
    };

    const themeNow = readTheme();
    if (themeNow) return themeNow;

    const start = performance.now();
    while (performance.now() - start < timeoutMs) {
      const theme = readTheme();
      if (theme) return theme;
      await new Promise((resolve) => setTimeout(resolve, 25));
    }
    return null;
  }

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    // Initialize DOM elements for recommendations and recent searches
    this.busquedas = this.querySelector(".js-search__busquedas");
    this.recientesWrapper = this.querySelector(
      ".js-search_busquedas-recientes"
    );
    this.recientesContent = this.recientesWrapper?.querySelector(
      ".js-search__recientes"
    );

    const { dialog } = this;
    const { signal } = this.#controller;

    // Core interactions: estos listeners son necesarios para que el buscador funcione
    // incluso cuando el resto de la inicialización se difiere.
    if (this.refs.searchInput) {
      this.refs.searchInput.addEventListener('input', this.search, { signal });
      this.refs.searchInput.addEventListener('keydown', this.onSearchKeyDown, { signal });
    }
    if (this.refs.resetButton) {
      this.refs.resetButton.addEventListener('click', this.resetSearch, { signal });
    }

    const initHeavy = () => {
      if (this.__initialized) return; this.__initialized = true;
      // heavy event wiring and data fetches below
      this.#wireEvents(dialog, signal);
      this.#postInit();
    };

    const win = /** @type {any} */ (globalThis);

    if ('IntersectionObserver' in win) {
      const io = new IntersectionObserver((entries, obs) => {
        for (const e of entries) {
          if (e.isIntersecting) { initHeavy(); obs.disconnect(); break; }
        }
      }, { rootMargin: '200px' });
      io.observe(this);
    } else {
      // Fallback: initialize on first user interaction
      const once = () => { initHeavy(); cleanup(); };
      const cleanup = () => {
        win.removeEventListener('pointerdown', once);
        win.removeEventListener('scroll', once);
        document.removeEventListener('keydown', once);
      };
      win.addEventListener('pointerdown', once);
      win.addEventListener('scroll', once);
      document.addEventListener('keydown', once);
    }

    // Light-weight bits safe to run immediately
    this.#lightInit();
  }

  /**
   * @param {DialogComponent | null} dialog
   * @param {AbortSignal} signal
   */
  #wireEvents(dialog, signal) {


    if (dialog) {
      document.addEventListener("keydown", this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      this.addEventListener("click", this.#handleModalClick, { signal });
    } else {
      document.addEventListener(ThemeEvents.megaMenuHover, this.#blurSearch, { signal });
      if (this.refs.searchInput) {
        this.refs.searchInput.addEventListener('click', this.#toggleDropdown.bind(this), { signal });
        this.refs.searchInput.addEventListener('focus', this.#toggleDropdown.bind(this), { signal });
        this.refs.searchInput.addEventListener('input', this.#toggleDropdown.bind(this), { signal });
      }
      document.addEventListener('click', this.#handleOutsideClick.bind(this), { signal });
    }
  }

  #lightInit() {
    if (this.refs.searchInput && this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }
    const results = this.refs.predictiveSearchResults.firstElementChild;
    if (results) {
      this.#resizeObserver.observe(results);
    }
    // Early hook: open dropdown on focus/input immediately
    if (this.refs.searchInput) {
      const openNow = () => {
        const wrapper = this.refs.predictiveSearchResults?.parentElement;
        if (wrapper) wrapper.classList.add('open');
      };
      this.refs.searchInput.addEventListener('focus', openNow, { passive: true });
      this.refs.searchInput.addEventListener('input', openNow, { passive: true });
    }
  }

  #postInit() {
    this.#getRecentlyViewed();
    this.showRecomendados();
  }
  
  /**
   * Toggle dropdown open state on input click (for non-modal)
   */
  #toggleDropdown() {
    const wrapper = this.refs.predictiveSearchResults?.parentElement;
    if (wrapper) {
      wrapper.classList.add('open');
    }
  }

  /**
   * Close dropdown if open (for non-modal)
   */
  #closeDropdown() {
    const wrapper = this.refs.predictiveSearchResults?.parentElement;
    if (wrapper) {
      wrapper.classList.remove('open');
    }
  }
  
  /**
   * Close dropdown when clicking outside (for non-modal)
   * @param {MouseEvent} event
   */
  #handleOutsideClick(event) {
    const wrapper = this.refs.predictiveSearchResults?.parentElement;
    if (wrapper && wrapper.classList.contains('open')) {
      const target = /** @type {EventTarget | null} */ (event.target);
      // Don't close if clicking inside the search component
      if (target instanceof Node && !this.contains(target)) {
        wrapper.classList.remove('open');
      }
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      try {
        this.refs.searchInput.focus({ preventScroll: true });
      } catch {
        this.refs.searchInput.focus();
      }
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      console.log(this.dialog);
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');

        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        item.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
      } else {
        item.removeAttribute('aria-selected');
      }
    }
    // Evita reenfocar el input en cada actualización si no estamos navegando con teclado.
    // El reenfoque continuo puede provocar pequeños desplazamientos verticales.
    if (this.#isKeyboardNavigation && document.activeElement !== this.refs.searchInput) {
      try {
        this.refs.searchInput.focus({ preventScroll: true });
      } catch {
        this.refs.searchInput.focus();
      }
    }
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter':
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          // `Theme` puede no estar listo aún si el JS global se retrasa.
          // Fallback razonable a /search si no hay rutas.
          const win = /** @type {any} */ (globalThis);
          // eslint-disable-next-line no-undef
          const searchBase = win.Theme?.routes?.search_url || (typeof Theme !== 'undefined' ? Theme?.routes?.search_url : null) || '/search';
          const searchUrl = new URL(searchBase, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    // Remove any recently viewed elements inserted into the list
    const recentlyViewedNodes = Array.from(this.querySelectorAll('[ref="recentlyViewedWrapper"]'));
    if (recentlyViewedNodes.length === 0) return;

    recentlyViewedNodes.forEach((node) => {
      if (node instanceof HTMLElement) {
        // Try graceful removal if an animation is defined
        if (typeof onAnimationEnd === 'function') {
          node.classList.add('removing');
          onAnimationEnd(node, () => node.remove());
        } else {
          node.remove();
        }
      }
    });

    // If the container is now empty, hide its preceding title (if any)
    const parentList = recentlyViewedNodes[0]?.parentElement;
    if (parentList instanceof HTMLElement && parentList.children.length === 0) {
      const maybeTitle = parentList.previousElementSibling;
      if (maybeTitle instanceof HTMLElement && maybeTitle.classList.contains('predictive-search-results__title')) {
        maybeTitle.classList.add('hidden');
      }
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((arg = true) => {
    // If called via event handler, treat first arg as Event and avoid keeping focus
    const isEvent = arg && typeof arg === 'object';
    const keepFocus = isEvent ? false : Boolean(arg);

    if (keepFocus) {
      try {
        this.refs.searchInput.focus({ preventScroll: true });
      } catch {
        this.refs.searchInput.focus();
      }
    } else {
      try {
        this.refs.searchInput.blur();
      } catch {}
    }

    // Close the dropdown wrapper if open
    this.#closeDropdown();

    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // Algunos navegadores no exponen `inputType`; en ese caso, asumimos input válido.
    // Si `inputType` existe y está vacío, no es un cambio de texto real.
    if (event && typeof event === 'object' && 'inputType' in event && !event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    if (searchTerm) {
      this.#saveSearchTerm(searchTerm);
    }

    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const resultsInner = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');
      if (resultsInner instanceof HTMLElement) {
        resultsInner.scrollTop = 0;
      }

      const formContent = this.querySelector('.predictive-search-form__content');
      if (formContent instanceof HTMLElement) {
        formContent.scrollTop = 0;
      }
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const theme = await this.#waitForTheme();
    if (!theme?.routes?.predictive_search_url) return;

    const url = new URL(theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);

        // Filter collections based on show_collections setting
        this.#filterCollections();
        // Set up tabbed behavior (Products / Collections)
        this.#setupResultsTabs();

        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const theme = await this.#waitForTheme();
    if (!theme?.routes?.search_url) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  /**
   * Fetch recently viewed products using the section renderer and update the results container.
   */
  #getRecentlyViewed = async () => {
    const { predictiveSearchResults } = this.refs;
    // Get the initial height before the results are rendered
    const abortController = this.#createAbortController();

    const resultsMarkup = await this.#getRecentlyViewedProductsMarkup();
    if (!resultsMarkup) return;

    const parsedNextPage = new DOMParser().parseFromString(resultsMarkup, 'text/html');
    const recentlyViewedProductsHtml = parsedNextPage.getElementById('predictive-search-products');
    if (!recentlyViewedProductsHtml) return;

    for (const child of recentlyViewedProductsHtml.children) {
      if (child instanceof HTMLElement) {
        child.setAttribute('ref', 'recentlyViewedWrapper');
      }
    }

    const collectionElement = predictiveSearchResults.querySelector('#predictive-search-products');
    if (!collectionElement) return;

    if (this.refs.recentlyViewedWrapper) {
      this.refs.recentlyViewedWrapper.remove();
    }

    if (abortController.signal.aborted) return;
    // Prepend the recently viewed products to the collection
    collectionElement.prepend(...recentlyViewedProductsHtml.children);
  };

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    
    // Filter collections based on show_collections setting
    this.#filterCollections();
    // Set up tabbed behavior (Products / Collections)
    this.#setupResultsTabs();
    
    this.#resetScrollPositions();
    
    // Re-initialize elements after morph
    this.busquedas = this.querySelector(".js-search__busquedas");
    this.recientesWrapper = this.querySelector(
      ".js-search_busquedas-recientes"
    );
    this.recientesContent = this.recientesWrapper?.querySelector(
      ".js-search__recientes"
    );
    
    // Show recommendations and recent searches again
    this.showRecomendados();
  };

  /**
   * Single tab for switching between Products and Collections results.
   */
  #setupResultsTabs() {
    // Only run if show_collections is true
    const showCollectionsAttr = this.getAttribute('data-show-collections') || '';
    const showCollections = showCollectionsAttr === 'true';
    if (!showCollections) return;

    const resultsInner = this.querySelector('.predictive-search-results__inner');
    if (!resultsInner) return;

    // Collect title → list pairs
    /** @type {{ titleEl: HTMLElement, listEl: HTMLElement, label: string }[]} */
    const pairs = [];
    const titles = resultsInner.querySelectorAll('.predictive-search-results__title');
    titles.forEach((titleEl) => {
      if (!(titleEl instanceof HTMLElement)) return;
      const listEl = titleEl.nextElementSibling;
      if (!listEl || !(listEl instanceof HTMLElement)) return;
      if (!listEl.classList.contains('predictive-search-results__list')) return;
      const label = titleEl.textContent?.trim() || 'Results';
      pairs.push({ titleEl, listEl, label });
    });

    if (pairs.length <= 1) return; // No need for tabs with ≤ 1 section

    // Avoid duplicating the tab bar
    let tabBar = resultsInner.querySelector('.predictive-search-tabs');
    if (!tabBar) {
      tabBar = document.createElement('div');
      tabBar.className = 'predictive-search-tabs';
      tabBar.setAttribute('role', 'tablist');
      // Insert tab bar before the first title if possible, otherwise prepend to results
      const firstPair = pairs[0];
      if (firstPair && firstPair.titleEl && firstPair.titleEl.parentElement) {
        firstPair.titleEl.parentElement.insertBefore(tabBar, firstPair.titleEl);
      } else {
        resultsInner.prepend(tabBar);
      }
    } else {
      tabBar.innerHTML = '';
    }

    // Hide original titles; control visibility via tabs
    pairs.forEach(({ titleEl }) => titleEl.classList.add('hidden'));

    // Create tabs
    pairs.forEach(({ label, listEl }, index) => {
      const targetList = listEl;
      if (!(targetList instanceof HTMLElement)) return;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'predictive-search-tab';
      btn.textContent = label;
      btn.setAttribute('role', 'tab');
      // Ensure the list has an id for aria-controls
      if (!targetList.id) {
        targetList.id = `ps-list-${Math.random().toString(36).slice(2, 8)}`;
      }
      // @ts-ignore - targetList is ensured HTMLElement above
      btn.setAttribute('aria-controls', targetList.id);
      btn.setAttribute('aria-selected', index === 0 ? 'true' : 'false');

      btn.addEventListener('click', () => {
        // Deactivate all
        tabBar.querySelectorAll('.predictive-search-tab').forEach((b) => {
          if (b instanceof HTMLElement) {
            b.classList.remove('is-active');
            b.setAttribute('aria-selected', 'false');
          }
        });
        pairs.forEach(({ listEl: l }) => {
          if (l instanceof HTMLElement) l.classList.add('hidden');
        });

        // Activate current
        btn.classList.add('is-active');
        btn.setAttribute('aria-selected', 'true');
        targetList.classList.remove('hidden');
      });

      tabBar.appendChild(btn);
    });

    // Show first list, hide others
    pairs.forEach(({ listEl }, index) => {
      if (index === 0) {
        listEl.classList.remove('hidden');
      } else {
        listEl.classList.add('hidden');
      }
    });

    // Mark first tab active
    const firstTab = tabBar.querySelector('.predictive-search-tab');
    if (firstTab instanceof HTMLElement) {
      firstTab.classList.add('is-active');
      firstTab.setAttribute('aria-selected', 'true');
    }
  }

  /**
   * Closes the predictive search.
   */
  #blurSearch = () => {
    this.refs.searchInput.blur();
  };

  /**
   * Save a search term to recent searches (in-memory storage)
   * @param {string} searchTerm - The search term to save
   */
  #saveSearchTerm(searchTerm) {
    if (!searchTerm || searchTerm.length === 0) return;

    // Get current searches from localStorage
    const busquedasStorage = localStorage.getItem("busquedas");
    const currentSearches = busquedasStorage ? JSON.parse(busquedasStorage) : [];

    // Remove if it already exists to avoid duplicates
    const updatedSearches = currentSearches.filter(
      // @ts-ignore - JavaScript file, type inference works at runtime
      (term) => term !== searchTerm
    );

    // Add to the beginning
    updatedSearches.unshift(searchTerm);

    // Keep only the last 10 searches
    const finalSearches = updatedSearches.slice(0, 10);

    // Save back to localStorage
    localStorage.setItem("busquedas", JSON.stringify(finalSearches));

    // Update in-memory array for immediate use
    this.#recentSearches = finalSearches;
  }

  /**
   * Filter collections based on show_collections setting
   */
  #filterCollections() {
    const showCollectionsAttr = this.getAttribute('data-show-collections') || '';
    const showCollections = showCollectionsAttr === 'true';
    
    if (!showCollections) {
      const allProductsContainers = this.querySelectorAll('.predictive-search-results__products');
      
      allProductsContainers.forEach((productsContainer) => {
        if (productsContainer instanceof HTMLElement) {
          // Check if this container has collections wrapper (not products wrapper)
          const hasCollections = productsContainer.querySelector('.predictive-search-results__wrapper-collections');
          const hasProducts = productsContainer.querySelector('.predictive-search-results__wrapper-products');
          
          // If it has collections but no products, hide the entire container
          if (hasCollections && !hasProducts) {
            productsContainer.style.display = 'none';
          } else if (hasCollections) {
            // If it has both, just hide the collections wrapper and its title
            const collectionsWrapper = productsContainer.querySelector('.predictive-search-results__wrapper-collections');
            if (collectionsWrapper instanceof HTMLElement) {
              collectionsWrapper.style.display = 'none';
              // Find and hide the title before the collections wrapper
              const title = productsContainer.querySelector('h4.predictive-search-results__title');
              if (title instanceof HTMLElement && title.nextElementSibling === collectionsWrapper) {
                title.style.display = 'none';
              }
            }
          }
        }
      });
    }
  }

  /**
   * Show recommended searches and recent searches
   */
  showRecomendados() {
    // Show static recommended searches
    if (this.busquedas) {
      this.busquedas.classList.remove("hidden");
      // Set up events for recommended items after showing them
      this.setEventRecomendados();
    }

    // Always show recent searches from localStorage (regardless of current search state)
    const busquedasStorage = localStorage.getItem("busquedas");
    const busquedaReciente = busquedasStorage ? JSON.parse(busquedasStorage) : [];

    // Update in-memory array
    this.#recentSearches = busquedaReciente;

    // Always check and show recent searches if they exist
    if (this.recientesWrapper) {
      if (busquedaReciente.length > 0) {
        this.recientesWrapper.classList.remove("hidden");
        const slideReciente = busquedaReciente.slice(0, 3); // Get the 3 most recent

        const htmlReciente = slideReciente
          .map(
            // @ts-ignore - JavaScript file, type inference works at runtime
            (element) => `
            <div class="js-search__recomendadas-item">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M12.5 9.75C12.5 8.23122 11.2688 7 9.75 7H4.56066L6.78033 9.21967C7.07322 9.51256 7.07322 9.98744 6.78033 10.2803C6.48744 10.5732 6.01256 10.5732 5.71967 10.2803L2.21967 6.78033C1.92678 6.48744 1.92678 6.01256 2.21967 5.71967L5.71967 2.21967C6.01256 1.92678 6.48744 1.92678 6.78033 2.21967C7.07322 2.51256 7.07322 2.98744 6.78033 3.28033L4.56066 5.5L9.75 5.5C12.0972 5.5 14 7.40279 14 9.75C14 12.0972 12.0972 14 9.75 14H8.75C8.33579 14 8 13.6642 8 13.25C8 12.8358 8.33579 12.5 8.75 12.5H9.75C11.2688 12.5 12.5 11.2688 12.5 9.75Z" fill="currentColor"/>
              </svg>
              <p>${element}</p>
            </div>
          `
          )
          .join("");

        if (this.recientesContent) {
          this.recientesContent.innerHTML = htmlReciente;
          // Set up events for recent search items (from localStorage)
          this.setEventRecientes();
        }
      } else {
        // Only hide if there are truly no recent searches
        this.recientesWrapper.classList.add("hidden");
      }
    }
  }

  /**
   * Set up event listeners for recent search items
   */
  setEventRecientes() {
    if (!this.recientesContent) return;

    this.recientesContent
      .querySelectorAll(".js-search__recomendadas-item")
      .forEach((element) => {
        element.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          const text = element.querySelector("p");
          if (text && this.refs.searchInput) {
            this.refs.searchInput.value = text.textContent;
            const inputEvent = new InputEvent("input", {
              inputType: "insertText",
              data: text.textContent
            });
            this.refs.searchInput.dispatchEvent(inputEvent);
          }
        }, true); // Use capture phase
      });
  }

  /**
   * Set up event listeners for recommended search items
   */
  setEventRecomendados() {
    if (this.busquedas) {
      this.busquedas
        .querySelectorAll(".js-search__recomendadas-item")
        .forEach((element) => {
          element.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            const text = element.querySelector("p");
            if (text && this.refs.searchInput) {
              this.refs.searchInput.value = text.textContent;
              const inputEvent = new InputEvent("input", {
                inputType: "insertText",
                data: text.textContent
              });
              this.refs.searchInput.dispatchEvent(inputEvent);
            }
          }, true); // Use capture phase
        });
    }
  }
}

if (!customElements.get("predictive-search-component")) {
  customElements.define(
    "predictive-search-component",
    PredictiveSearchComponent
  );
}